import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VendorCard = ({ 
  vendor, 
  onMessage, 
  onCheckAvailability, 
  onToggleFavorite, 
  onToggleComparison,
  isSelectedForComparison = false,
  canAddToComparison = true
}) => {
  const [isFavorited, setIsFavorited] = useState(vendor.isFavorited || false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    onToggleFavorite?.(vendor.id, !isFavorited);
  };

  const handleToggleComparison = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleComparison?.(vendor);
  };

  const handleMessage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onMessage?.(vendor.id);
  };

  const handleCheckAvailability = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCheckAvailability?.(vendor.id);
  };

  const renderRating = () => {
    const fullStars = Math.floor(vendor.rating);
    const hasHalfStar = vendor.rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center space-x-1">
        {[...Array(fullStars)].map((_, i) => (
          <Icon key={i} name="Star" size={14} color="#DAA520" />
        ))}
        {hasHalfStar && <Icon name="Star" size={14} color="#DAA520" className="opacity-50" />}
        {[...Array(emptyStars)].map((_, i) => (
          <Icon key={i} name="Star" size={14} color="#E8E2DD" />
        ))}
        <span className="text-sm text-text-secondary font-data ml-1">
          ({vendor.reviewCount})
        </span>
      </div>
    );
  };

  return (
    <div className="group relative">
      <Link to="/vendor-profile" className="block">
        <div className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-200 overflow-hidden border border-border-light group-hover:border-primary-200">
          {/* Image Container */}
          <div className="relative aspect-[4/3] overflow-hidden">
            <Image
              src={vendor.image}
              alt={`${vendor.name} portfolio`}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
            />
            
            {/* Loading Skeleton */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-surface-200 animate-pulse flex items-center justify-center">
                <Icon name="Camera" size={32} color="#8B7B6F" />
              </div>
            )}

            {/* Overlay Actions */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200">
              <div className="absolute top-3 right-3 flex items-center space-x-2">
                {/* Compare Button */}
                <button
                  onClick={handleToggleComparison}
                  disabled={!canAddToComparison && !isSelectedForComparison}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                    isSelectedForComparison
                      ? 'bg-secondary text-white shadow-medium'
                      : canAddToComparison 
                        ? 'bg-white/90 text-text-secondary hover:bg-white hover:text-secondary' :'bg-white/50 text-text-muted cursor-not-allowed'
                  }`}
                >
                  <Icon name="BarChart3" size={14} color="currentColor" />
                </button>

                {/* Favorite Button */}
                <button
                  onClick={handleToggleFavorite}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                    isFavorited
                      ? 'bg-accent text-white shadow-medium'
                      : 'bg-white/90 text-text-secondary hover:bg-white hover:text-accent'
                  }`}
                >
                  <Icon name="Heart" size={16} color="currentColor" />
                </button>
              </div>

              {/* Quick Actions */}
              <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMessage}
                    className="bg-white/90 hover:bg-white text-text-primary flex-1"
                  >
                    Message
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleCheckAvailability}
                    className="flex-1"
                  >
                    Check Availability
                  </Button>
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col space-y-1">
              {vendor.isVerified && (
                <div className="bg-success text-success-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                  <Icon name="CheckCircle" size={12} color="currentColor" />
                  <span>Verified</span>
                </div>
              )}
              {vendor.isAvailable && (
                <div className="bg-secondary text-secondary-foreground px-2 py-1 rounded-full text-xs font-medium">
                  Available
                </div>
              )}
              {vendor.isFeatured && (
                <div className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
                  Featured
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-3">
            {/* Header */}
            <div className="space-y-1">
              <h3 className="font-heading font-semibold text-lg text-text-primary group-hover:text-primary transition-colors">
                {vendor.name}
              </h3>
              <p className="text-sm text-secondary font-medium">
                {vendor.culturalSpecialization}
              </p>
            </div>

            {/* Rating */}
            <div className="flex items-center justify-between">
              {renderRating()}
              <div className="text-sm text-text-secondary font-caption">
                {vendor.experienceYears}+ years
              </div>
            </div>

            {/* Location */}
            <div className="flex items-center space-x-2">
              <Icon name="MapPin" size={14} color="#6B5B4F" />
              <span className="text-sm text-text-secondary font-caption">
                {vendor.location}
              </span>
              <span className="text-xs text-text-muted">•</span>
              <span className="text-sm text-text-secondary font-caption">
                {vendor.distance} miles away
              </span>
            </div>

            {/* Services */}
            <div className="flex flex-wrap gap-1">
              {vendor.services.slice(0, 3).map((service, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-surface-100 text-text-secondary text-xs rounded-full font-caption"
                >
                  {service}
                </span>
              ))}
              {vendor.services.length > 3 && (
                <span className="px-2 py-1 bg-surface-100 text-text-secondary text-xs rounded-full font-caption">
                  +{vendor.services.length - 3} more
                </span>
              )}
            </div>

            {/* Pricing */}
            <div className="flex items-center justify-between pt-2 border-t border-border-light">
              <div className="flex-1">
                <span className="text-sm text-text-secondary font-caption">Starting from</span>
                <div className="font-heading font-bold text-lg text-primary">
                  ${vendor.startingPrice.toLocaleString()}
                </div>
              </div>
              
              {/* Compare Button - Next to Pricing */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleToggleComparison}
                  disabled={!canAddToComparison && !isSelectedForComparison}
                  className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${
                    isSelectedForComparison
                      ? 'bg-secondary text-secondary-foreground'
                      : canAddToComparison 
                        ? 'bg-surface-100 text-text-secondary hover:bg-secondary hover:text-secondary-foreground'
                        : 'bg-surface-50 text-text-muted cursor-not-allowed'
                  }`}
                >
                  <Icon name="BarChart3" size={12} color="currentColor" />
                  <span>{isSelectedForComparison ? 'Selected' : 'Compare'}</span>
                </button>
                
                {/* Recent Booking Indicator */}
                {vendor.recentBookings > 0 && (
                  <div className="text-right">
                    <div className="text-xs text-success font-medium">
                      {vendor.recentBookings} recent bookings
                    </div>
                    <div className="text-xs text-text-muted font-caption">this month</div>
                  </div>
                )}
              </div>
            </div>

            {/* Languages */}
            {vendor.languages && vendor.languages.length > 0 && (
              <div className="flex items-center space-x-2 pt-2">
                <Icon name="Globe" size={14} color="#6B5B4F" />
                <span className="text-xs text-text-secondary font-caption">
                  Speaks: {vendor.languages.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default VendorCard;